import { api } from './client';

export type MrnStatus = 'Draft' | 'Pending' | 'Pending HOD' | 'Approved' | 'Rejected';
export type Priority = 'Low' | 'Medium' | 'High' | 'Urgent';
export type Authority = 'HOD' | 'Manager' | 'Director' | 'Chairman';

export interface MrnItem {
  id?: number;
  itemId?: number | null;
  itemName: string;
  qty: number | string;
  unit: string;
  remarks?: string | null;
  /** Filled on the HOD pending list: item-master details and current warehouse stock. */
  itemCode?: string | null;
  barcode?: string | null;
  imageUrl?: string | null;
  currentStock?: number | null;
  availableStock?: number | null;
}

export interface SlaBadge {
  state: 'ok' | 'warn' | 'overdue' | 'inactive';
  stepId: string;
  hours: number;
  elapsedHours: number;
  pct: number;
}

export interface Mrn {
  id: number;
  mrnNo: string;
  mrnDate: string;
  /** Short subject, carried forward MRN → PR → PO → GRN. */
  subject?: string | null;
  purpose: string;
  refDoc: string | null;
  department: string;
  raisedBy: string;
  priority: Priority;
  requiredBy: string | null;
  approvalAuthority: Authority;
  remarks: string | null;
  status: MrnStatus;
  rejectionReason: string | null;
  source: 'Manual' | 'Sales-Short' | 'AI-Auto';
  enqRef: string | null;
  isAutoGenerated: boolean;
  items: MrnItem[];
  sla?: SlaBadge | null;
}

export interface MrnListResult {
  data: Mrn[];
  total: number;
  page: number;
  pageSize: number;
}

export interface MrnFilter {
  status?: string;
  dept?: string;
  from?: string;
  to?: string;
  search?: string;
  page?: number;
  pageSize?: number;
  /** When true, only return MRNs that have no PR generated yet */
  noPr?: boolean;
}

export async function listMrns(filter: MrnFilter = {}): Promise<MrnListResult> {
  const r = await api.get<MrnListResult>('/mrn', { params: filter });
  return r.data;
}

export async function pendingMrns(): Promise<Mrn[]> {
  const r = await api.get<Mrn[]>('/mrn/pending');
  return r.data;
}

export async function getMrn(id: number): Promise<Mrn> {
  const r = await api.get<Mrn>(`/mrn/${id}`);
  return r.data;
}

export async function createMrn(payload: Partial<Mrn> & { items: MrnItem[] }): Promise<Mrn> {
  const r = await api.post<Mrn>('/mrn', payload);
  return r.data;
}

export async function updateMrn(id: number, payload: Partial<Mrn>): Promise<Mrn> {
  const r = await api.patch<Mrn>(`/mrn/${id}`, payload);
  return r.data;
}

export async function deleteMrn(id: number): Promise<void> {
  await api.delete(`/mrn/${id}`);
}

export async function submitMrn(id: number): Promise<Mrn> {
  const r = await api.post<Mrn>(`/mrn/${id}/submit`);
  return r.data;
}

export async function approveMrn(id: number): Promise<Mrn> {
  const r = await api.post<Mrn>(`/mrn/${id}/approve`);
  return r.data;
}

export async function rejectMrn(id: number, reason: string): Promise<Mrn> {
  const r = await api.post<Mrn>(`/mrn/${id}/reject`, { reason });
  return r.data;
}

export async function bulkApproveMrn(ids: number[]): Promise<{ approved: number }> {
  const r = await api.post<{ approved: number }>('/mrn/bulk-approve', { ids });
  return r.data;
}
